import { useCallback, useState } from 'react';

import { LocalStorageKeyType } from '~/enums/LocalStorageKeyType';

const defaultOptions = {
  /** Skip updating local storage but still updates React state */
  skipUpdateLocalStorage: false,
};

/**
 * This hooks reads/updates both to localStorage and React state (to participate in React's render lifecycle).  Keys must be defined/maintained in LocalStorageKeyType.
 *
 * The setter is stable and, like React's `useState`, accepts either the next
 * value or an updater `(prev) => next` — so callers can update based on the
 * latest value without closing over it (and can list the setter in effect
 * dependency arrays safely).
 *
 * Error handling is internalized with the following behaviors (can be exposed in the future after usage patterns mature):
 * - return initial value if localStorage is not available or errors
 * - swallow error if failing to write to localStorage
 **/
export const useLocalStorageState = <V>(
  key: LocalStorageKeyType,
  initialValue: V,
  options = defaultOptions,
): [V, (value: V | ((prev: V) => V)) => void] => {
  const [storedValue, setStoredValue] = useState<V>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const { skipUpdateLocalStorage } = options;
  const setValue = useCallback(
    (value: V | ((prev: V) => V)) => {
      // Resolve updaters through React's state updater so successive calls in
      // the same tick chain off each other's result (rather than a value
      // captured at render), then mirror the resolved value to localStorage.
      setStoredValue((prev) => {
        const nextValue = value instanceof Function ? value(prev) : value;
        try {
          if (!skipUpdateLocalStorage) {
            window.localStorage.setItem(key, JSON.stringify(nextValue));
          }
        } catch {}
        return nextValue;
      });
    },
    [key, skipUpdateLocalStorage],
  );

  return [storedValue, setValue];
};
